// Pure derivations behind `useEventCapacity`. The hook owns fetching + memoisation; every capacity rule
// lives here as a small, individually-testable function so the hook reads as orchestration. The deep
// rationale (Lifetime-Σ, the two-writer pool.sold problem, holds≠pool, the de-double-count) lives in
// docs/pools_business_logic.md and ./CLAUDE.md — comments here stay to one line + a pointer.

import { CapacityPool, CapacityTicketType, CapacityType, CapacityTotals } from './types';

/**
 * The pools response as decoded by `useEventCapacity` — bare `humps.camelizeKeys`, which lowercases the
 * all-caps backend `ID` to `iD` (every key, incl. members). The shared `Pool` type (TicketDetails) is decoded
 * by the project `camelize` (keepUpperCase → `ID`) elsewhere, so it does NOT describe this shape. Reading
 * `pool.ID` here once produced an `undefined` poolId that broke the dashboard tile click + the Send clamp.
 */
export interface RawPool {
	iD: number;
	name: string;
	description?: string;
	totalAmount: number;
	ticketTypes?: { iD: number; name: string }[];
}

interface EventTypeInfo {
	description: string;
	holdsAmount: number;
	maxPrice: number;
}

export const EMPTY_TOTALS: CapacityTotals = {
	capacity: 0,
	effectiveCapacity: 0,
	sold: 0,
	comped: 0,
	issued: 0,
	holds: 0,
	available: 0,
	expired: 0,
	poolAllocated: 0,
	poolSold: 0,
	poolRemaining: 0,
};

// ── Name-keyed lookups (name is the reliable join key across the public/highlights/pools responses) ─────────

/** Snapshot issued per type — `Highlights.ticket_types_inventory[].sold` keyed by name. */
export function buildIssuedByName(inventory: { name: string; sold: number }[] = []): Record<string, number> {
	const map: Record<string, number> = {};
	for (const inv of inventory) map[inv.name] = inv.sold;
	return map;
}

/** Time-series issued per type — `Σ Highlights.sales[].ticket_types[].sold` keyed by name (fallback source). */
export function buildSalesIssuedByName(
	sales: { ticket_types: { name: string; sold: number }[] }[] = []
): Record<string, number> {
	const map: Record<string, number> = {};
	for (const sale of sales) for (const stt of sale.ticket_types) map[stt.name] = (map[stt.name] ?? 0) + stt.sold;
	return map;
}

/** Each type's own sellable `amount` keyed by name — the base for a pool's sellable-remaining bound. */
export function buildAmountByName(publicTypes: CapacityTicketType[] = []): Record<string, number> {
	const map: Record<string, number> = {};
	for (const tt of publicTypes) map[tt.name] = tt.amount;
	return map;
}

/** Which types are past their on-sale window — an expired tier can't draw shared pool capacity (→ slack). */
export function buildExpiredByName(publicTypes: CapacityTicketType[] = [], now: Date): Record<string, boolean> {
	const map: Record<string, boolean> = {};
	for (const tt of publicTypes) map[tt.name] = !!(tt.offSellingTime && new Date(tt.offSellingTime) < now);
	return map;
}

/**
 * Issued per type, resolved ONCE via the three-tier fallback: snapshot > time-series > public `tt.sold`.
 * The single issued figure BOTH the per-type `sold` and pool Lifetime-Σ usage read, so they never diverge.
 * Includes comps (a comped seat is a seat taken; the snapshot already counts them).
 */
export function resolveIssuedByName(
	publicTypes: CapacityTicketType[] = [],
	issuedByName: Record<string, number>,
	salesIssuedByName: Record<string, number>
): Record<string, number> {
	const map: Record<string, number> = {};
	for (const tt of publicTypes) {
		const snapshot = issuedByName[tt.name] ?? 0;
		const timeSeries = salesIssuedByName[tt.name] ?? 0;
		map[tt.name] = snapshot > 0 ? snapshot : timeSeries > 0 ? timeSeries : tt.sold;
	}
	return map;
}

/** Description + per-type reserve (`holdsAmount`) + PWYW ceiling price, keyed by event-API ticket-type id. */
export function buildEventInfoById(
	eventTicketTypes: { ID: number; description?: string; holdsAmount?: number; maxPrice?: number | null }[] = []
): Record<number, EventTypeInfo> {
	const map: Record<number, EventTypeInfo> = {};
	for (const tt of eventTicketTypes) {
		map[tt.ID] = { description: tt.description ?? '', holdsAmount: tt.holdsAmount ?? 0, maxPrice: tt.maxPrice ?? 0 };
	}
	return map;
}

// ── Pools ───────────────────────────────────────────────────────────────────────────────────────────────

/**
 * One pool with usage derived from Lifetime-Σ (Σ member issued) — never the unstable backend `pool.sold`
 * scalar (docs/pools_business_logic.md §1). Splits the shared remainder into `sellableRemaining` (what
 * on-sale members can actually draw) and `slack` (oversized-pool excess + expired members' unsold seats).
 */
export function derivePool(
	pool: RawPool,
	resolvedIssuedByName: Record<string, number>,
	amountByName: Record<string, number>,
	expiredByName: Record<string, boolean>
): CapacityPool {
	const members = pool.ticketTypes ?? [];
	const memberNames = members.map((m) => m.name);
	const lifetimeSold = memberNames.reduce((sum, name) => sum + (resolvedIssuedByName[name] ?? 0), 0);
	const poolRemaining = Math.max(0, pool.totalAmount - lifetimeSold);
	const onSaleMemberRemaining = memberNames.reduce(
		(sum, name) =>
			sum + (expiredByName[name] ? 0 : Math.max(0, (amountByName[name] ?? 0) - (resolvedIssuedByName[name] ?? 0))),
		0
	);
	const sellableRemaining = Math.min(poolRemaining, onSaleMemberRemaining);
	const memberAmountSum = memberNames.reduce((sum, name) => sum + (amountByName[name] ?? 0), 0);
	return {
		poolId: pool.iD,
		name: pool.name,
		description: pool.description ?? '',
		totalAmount: pool.totalAmount,
		sold: lifetimeSold,
		poolRemaining,
		sellableRemaining,
		slack: poolRemaining - sellableRemaining,
		cappedTotal: Math.min(pool.totalAmount, memberAmountSum), // most the pool can ever issue across its members
		memberTypeIds: members.map((m) => m.iD),
		memberNames,
	};
}

export function derivePools(
	rawPools: RawPool[],
	resolvedIssuedByName: Record<string, number>,
	amountByName: Record<string, number>,
	expiredByName: Record<string, boolean>
): CapacityPool[] {
	return rawPools.map((pool) => derivePool(pool, resolvedIssuedByName, amountByName, expiredByName));
}

/** Pool keyed by member ticket-type name — the per-type capping source. */
export function buildPoolByMemberName(pools: CapacityPool[]): Record<string, CapacityPool> {
	const map: Record<string, CapacityPool> = {};
	for (const pool of pools) for (const name of pool.memberNames) map[name] = pool;
	return map;
}

// ── Per type ────────────────────────────────────────────────────────────────────────────────────────────

interface DeriveTypeDeps {
	eventInfoById: Record<number, EventTypeInfo>;
	resolvedIssuedByName: Record<string, number>;
	expiredByName: Record<string, boolean>;
	poolByMemberName: Record<string, CapacityPool>;
	compedByType: Record<string, number>;
}

/**
 * One ticket type resolved against its pool. `available = min(typeRemaining, poolRemaining)` (0 if expired);
 * `poolBlocked` is the tier's own leftover the full pool can't sell (the "Pool full" slice). `holds` is the
 * per-type reserve from the EVENT API only — never the public `holds_amount` (a live comped count) or the pool.
 */
export function deriveType(tt: CapacityTicketType, deps: DeriveTypeDeps): CapacityType {
	const { eventInfoById, resolvedIssuedByName, expiredByName, poolByMemberName, compedByType } = deps;
	const info = eventInfoById[tt.iD];
	const isExpired = expiredByName[tt.name] ?? false;
	const sold = resolvedIssuedByName[tt.name] ?? 0;
	const holds = info?.holdsAmount ?? 0;
	const pool = poolByMemberName[tt.name] ?? null;

	const typeRemaining = Math.max(0, tt.amount - sold);
	const capped = pool ? Math.min(typeRemaining, pool.poolRemaining) : typeRemaining;
	const available = isExpired ? 0 : capped;
	const expired = isExpired ? capped : 0;

	return {
		typeId: tt.iD,
		name: tt.name,
		description: info?.description ?? '',
		initialPrice: tt.initialPrice,
		maxPrice: info?.maxPrice ?? 0,
		payWhatYouWant: tt.payWhatYouWant,
		isExpired,
		amount: tt.amount,
		sold,
		holds,
		compedCount: Math.min(compedByType[tt.name] ?? 0, sold),
		poolName: pool?.name ?? null,
		poolId: pool?.poolId ?? null,
		available,
		expired,
		poolBlocked: pool ? Math.max(0, typeRemaining - available - expired) : 0,
		trueCapacity: tt.amount + holds,
	};
}

export function deriveTypes(publicTypes: CapacityTicketType[] = [], deps: DeriveTypeDeps): CapacityType[] {
	return publicTypes.map((tt) => deriveType(tt, deps));
}

// ── Show-level totals ───────────────────────────────────────────────────────────────────────────────────

/**
 * Show-level roll-up. `issued` is the canonical numerator (Σ per-type resolved issued — NOT the show-level
 * Highlights `sold + comped`, which can disagree by the two-writer delta). `effectiveCapacity` is the
 * pool-capped, de-double-counted denominator. Available rolls up Σ pool sellableRemaining ONCE (every member
 * caps to the same bucket). See docs/pools_business_logic.md §3.
 */
export function deriveTotals(
	types: CapacityType[],
	pools: CapacityPool[],
	showLevel: { sold: number; comped: number }
): CapacityTotals {
	if (types.length === 0) return EMPTY_TOTALS;
	const hasPools = pools.length > 0;
	const { sold, comped } = showLevel;

	const capacity = types.reduce((s, t) => s + t.trueCapacity, 0);
	const issued = types.reduce((s, t) => s + t.sold, 0);
	const holds = types.reduce((s, t) => s + t.holds, 0);
	const expired = types.reduce((s, t) => s + t.expired, 0);

	const standaloneAvailable = types.reduce((s, t) => (t.poolName ? s : s + t.available), 0);
	const poolSellable = pools.reduce((s, p) => s + p.sellableRemaining, 0);
	const available = hasPools
		? standaloneAvailable + poolSellable
		: Math.max(0, capacity - sold - comped - holds - expired);

	const standaloneCapacity = types.reduce((s, t) => (t.poolName ? s : s + t.trueCapacity), 0);
	const memberHoldsByPool: Record<number, number> = {};
	for (const t of types) {
		if (t.poolId == null) continue;
		memberHoldsByPool[t.poolId] = (memberHoldsByPool[t.poolId] ?? 0) + t.holds;
	}
	const poolCapacity = pools.reduce((s, p) => s + p.cappedTotal + (memberHoldsByPool[p.poolId] ?? 0), 0);

	return {
		capacity,
		effectiveCapacity: standaloneCapacity + poolCapacity,
		sold,
		comped,
		issued,
		holds,
		available,
		expired,
		poolAllocated: pools.reduce((s, p) => s + p.totalAmount, 0),
		poolSold: pools.reduce((s, p) => s + p.sold, 0),
		poolRemaining: pools.reduce((s, p) => s + p.poolRemaining, 0),
	};
}
